import React, { useState } from 'react';
import { useTranslation } from 'localization';
import { StageDefinition } from 'types/arc';
import './ArcStepperMolecule.scss';

export interface ArcStepperMoleculeProps {
  stages: StageDefinition[];
  arcName: string;
  className?: string;
}

const ArcStepperMolecule: React.FC<ArcStepperMoleculeProps> = ({
  stages,
  arcName,
  className = '',
}) => {
  const { t } = useTranslation();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const classes = ['marketplace-arc-stepper-molecule', className]
    .filter(Boolean)
    .join(' ');

  const handleStageClick = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  if (stages.length === 0) {
    return (
      <div className={classes}>
        <p className="marketplace-arc-stepper-molecule__empty">
          {t('marketplace.approachDetail.arc.noStages')}
        </p>
      </div>
    );
  }

  const expandedStage = expandedIndex === null ? null : stages[expandedIndex];

  return (
    <div className={classes}>
      <div className="marketplace-arc-stepper-molecule__header">
        <span>{t('marketplace.approachDetail.arc.templateName', { name: arcName })}</span>
        <span className="marketplace-arc-stepper-molecule__stage-count">
          {t('marketplace.approachDetail.arc.stageCount', { count: stages.length })}
        </span>
      </div>

      <div className="marketplace-arc-stepper-molecule__stepper">
        {stages.map((stage, index) => {
          const isExpanded = expandedIndex === index;
          const stageClasses = [
            'marketplace-arc-stepper-molecule__stage',
            isExpanded ? 'marketplace-arc-stepper-molecule__stage--expanded' : '',
            stage.isTerminal ? 'marketplace-arc-stepper-molecule__stage--terminal' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <React.Fragment key={stage.id}>
              {index > 0 && (
                <div className="marketplace-arc-stepper-molecule__connector" />
              )}
              <button
                type="button"
                className={stageClasses}
                onClick={() => handleStageClick(index)}
                aria-expanded={isExpanded}
              >
                <span className="marketplace-arc-stepper-molecule__stage-circle">
                  {stage.icon ? (
                    <i className={`pi pi-${stage.icon}`} />
                  ) : (
                    index + 1
                  )}
                </span>
                <span
                  className="marketplace-arc-stepper-molecule__stage-name"
                  title={stage.fallbackName}
                >
                  {stage.fallbackName}
                </span>
                <span className="marketplace-arc-stepper-molecule__stage-type">
                  {t(
                    `marketplace.approachDetail.arc.stageTypes.${stage.type}`
                  )}
                </span>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {expandedStage && (
        <div className="marketplace-arc-stepper-molecule__detail">
          <div className="marketplace-arc-stepper-molecule__detail-item">
            <i className="pi pi-arrow-right-arrow-left" />
            <span>
              {t('marketplace.approachDetail.arc.transitionType', {
                type: t(
                  `marketplace.approachDetail.arc.transitionTypes.${expandedStage.transition.type}`
                ),
              })}
            </span>
          </div>

          {expandedStage.intents && expandedStage.intents.length > 0 && (
            <div className="marketplace-arc-stepper-molecule__detail-item">
              <i className="pi pi-flag" />
              <span>
                {t('marketplace.approachDetail.arc.intentCount', {
                  count: expandedStage.intents.length,
                })}
              </span>
            </div>
          )}

          {expandedStage.isTerminal && (
            <span className="marketplace-arc-stepper-molecule__terminal-badge">
              {t('marketplace.approachDetail.arc.terminal')}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ArcStepperMolecule;
